import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Label,
  Badge,
  Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';

import { api } from '../../../config';
import DualListPanel from '../../MenuAssigment/Components/DualListPanel';

const toPermissionItem = p => ({
  id: p.id,
  scope: p.scope,
  module: p.module,
  label: p.module ? `[${p.module}] ${p.scope}` : p.scope,
});

const sortByModuleThenScope = (a, b) => {
  const moduleCompare = (a.module || '').localeCompare(b.module || '');
  if (moduleCompare !== 0) return moduleCompare;
  return (a.scope || '').localeCompare(b.scope || '');
};

const RolePermissionAssignModal = ({ isOpen, toggle, token, selectedRole }) => {
  const isSuperAdmin = selectedRole?.name === 'Super Admin';

  const [modalLoading, setModalLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allPermissions, setAllPermissions] = useState([]);
  const [originalAssignedIds, setOriginalAssignedIds] = useState([]);
  const [assignedIds, setAssignedIds] = useState([]);
  const [leftSelection, setLeftSelection] = useState([]);
  const [rightSelection, setRightSelection] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [lastLeftIndex, setLastLeftIndex] = useState(null);
  const [lastRightIndex, setLastRightIndex] = useState(null);

  const resetState = () => {
    setAllPermissions([]);
    setOriginalAssignedIds([]);
    setAssignedIds([]);
    setLeftSelection([]);
    setRightSelection([]);
    setLeftSearch('');
    setRightSearch('');
    setHasChanges(false);
    setLastLeftIndex(null);
    setLastRightIndex(null);
  };

  useEffect(() => {
    if (!isOpen || !selectedRole || !token || isSuperAdmin) return;

    const loadAssignments = async () => {
      resetState();
      setModalLoading(true);

      try {
        const response = await fetch(
          `${api.API_URL}/role-permission/by-role/${selectedRole.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok)
          throw new Error('Failed to load role permission data');

        const result = await response.json();
        const assigned = (result.data?.assigned || []).map(toPermissionItem);
        const available = (result.data?.available || []).map(toPermissionItem);

        setAllPermissions(
          [...assigned, ...available].sort(sortByModuleThenScope)
        );
        const assignedIdList = assigned.map(p => String(p.id));
        setOriginalAssignedIds(assignedIdList);
        setAssignedIds(assignedIdList);
      } catch (error) {
        toast.error(`Error: ${error.message}`);
        toggle();
      } finally {
        setModalLoading(false);
      }
    };

    loadAssignments();
  }, [isOpen, selectedRole, token, isSuperAdmin]);

  useEffect(() => {
    if (!isOpen) return;

    const currentSet = new Set(assignedIds);
    const originalSet = new Set(originalAssignedIds);

    const isDifferent =
      assignedIds.length !== originalAssignedIds.length ||
      assignedIds.some(id => !originalSet.has(id)) ||
      originalAssignedIds.some(id => !currentSet.has(id));

    setHasChanges(isDifferent);
  }, [assignedIds, originalAssignedIds, isOpen]);

  useEffect(() => {
    setLastLeftIndex(null);
  }, [leftSearch]);

  useEffect(() => {
    setLastRightIndex(null);
  }, [rightSearch]);

  const handleClose = () => {
    resetState();
    toggle();
  };

  const assignedSet = new Set(assignedIds);

  const availablePermissions = allPermissions.filter(
    p =>
      !assignedSet.has(String(p.id)) &&
      p.label.toLowerCase().includes(leftSearch.toLowerCase())
  );

  const assignedPermissions = allPermissions.filter(
    p =>
      assignedSet.has(String(p.id)) &&
      p.label.toLowerCase().includes(rightSearch.toLowerCase())
  );

  const moveRight = () => {
    if (leftSelection.length === 0) return;
    setAssignedIds(prev => [...prev, ...leftSelection.map(String)]);
    setLeftSelection([]);
  };

  const moveAllRight = () => {
    const allAvailable = allPermissions
      .filter(p => !assignedSet.has(String(p.id)))
      .map(p => String(p.id));
    setAssignedIds(prev => [...prev, ...allAvailable]);
    setLeftSelection([]);
  };

  const moveLeft = () => {
    if (rightSelection.length === 0) return;
    const removeIds = new Set(rightSelection.map(String));
    setAssignedIds(prev => prev.filter(id => !removeIds.has(String(id))));
    setRightSelection([]);
  };

  const moveAllLeft = () => {
    setAssignedIds([]);
    setRightSelection([]);
  };

  const handleSave = async () => {
    if (!hasChanges) {
      toast.info('No changes to save');
      handleClose();
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${api.API_URL}/role-permission/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          roleId: selectedRole.id,
          permissionIds: assignedIds,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.errors?.[0] || 'Save operation failed'
        );
      }

      toast.success(
        `Permissions updated for ${selectedRole?.name || selectedRole?.id}`
      );
      handleClose();
    } catch (error) {
      toast.error(`Save failed: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size='xl'
      backdrop='static'
      centered
    >
      <ModalHeader toggle={handleClose} className='bg-light'>
        Manage Permissions — {selectedRole?.name || selectedRole?.id}
      </ModalHeader>
      <ModalBody>
        {isSuperAdmin ? (
          <Alert color='warning' className='mb-0'>
            <i className='ri-shield-star-line align-middle me-2'></i>
            Super Admin memegang semua permission (bypass). Assignment manual
            tidak diperlukan dan tidak dapat diubah untuk role ini.
          </Alert>
        ) : modalLoading ? (
          <div className='d-flex justify-content-center py-5'>
            <Spinner color='primary' />
          </div>
        ) : (
          <Row className='g-3'>
            <Col md={12}>
              <Label>Permission Assignment</Label>
              <Row className='align-items-center'>
                <Col xs={12}>
                  <div className='d-flex align-items-center flex-wrap gap-2 text-muted fs-12 mb-1'>
                    <i className='ri-information-line text-info fs-14'></i>
                    <span>Click</span> <span className='fw-medium'>select</span>
                    <span className='mx-1'>•</span>
                    <Badge color='light' className='text-dark border fs-10'>
                      Ctrl / ⌘
                    </Badge>
                    <span>toggle</span>
                    <span className='mx-1'>•</span>
                    <Badge color='light' className='text-dark border fs-10'>
                      Shift
                    </Badge>
                    <span>range select</span>
                  </div>
                </Col>
                <Col xs={12} md={5} className='order-1 order-md-1'>
                  <DualListPanel
                    title='Available Permissions'
                    items={availablePermissions}
                    selectedIds={leftSelection}
                    searchValue={leftSearch}
                    onSearchChange={setLeftSearch}
                    onSelectionChange={setLeftSelection}
                    lastIndex={lastLeftIndex}
                    onLastIndexChange={setLastLeftIndex}
                    emptyMessage='No available permissions'
                  />
                </Col>
                <Col
                  xs={12}
                  md={2}
                  className='order-2 order-md-2 d-flex flex-row flex-md-column justify-content-center align-items-center gap-2 my-2 my-md-0'
                >
                  <Button
                    color='primary'
                    size='sm'
                    onClick={moveAllRight}
                    disabled={availablePermissions.length === 0}
                    title='Move All'
                  >
                    <i className='ri-arrow-right-double-line fs-16'></i>
                  </Button>
                  <Button
                    color='primary'
                    size='sm'
                    onClick={moveRight}
                    disabled={leftSelection.length === 0}
                    title='Move Selected'
                  >
                    <i className='ri-arrow-right-s-line fs-16'></i>
                  </Button>
                  <Button
                    color='primary'
                    size='sm'
                    onClick={moveLeft}
                    disabled={rightSelection.length === 0}
                    title='Move Selected'
                  >
                    <i className='ri-arrow-left-s-line fs-16'></i>
                  </Button>
                  <Button
                    color='primary'
                    size='sm'
                    onClick={moveAllLeft}
                    disabled={assignedIds.length === 0}
                    title='Remove All'
                  >
                    <i className='ri-arrow-left-double-line fs-16'></i>
                  </Button>
                </Col>
                <Col xs={12} md={5} className='order-3 order-md-3'>
                  <DualListPanel
                    title='Assigned Permissions'
                    titleClassName='border rounded p-2 bg-primary text-white text-center mb-2 fw-medium fs-12'
                    items={assignedPermissions}
                    selectedIds={rightSelection}
                    searchValue={rightSearch}
                    onSearchChange={setRightSearch}
                    onSelectionChange={setRightSelection}
                    lastIndex={lastRightIndex}
                    onLastIndexChange={setLastRightIndex}
                    emptyMessage='No assigned permissions'
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color='light' onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleSave}
          disabled={isSuperAdmin || modalLoading || saving || !hasChanges}
        >
          {saving ? (
            <Spinner size='sm' className='me-2' />
          ) : (
            <i className='ri-save-line me-1'></i>
          )}
          Save Assignments
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RolePermissionAssignModal;
